import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { adminMe } from '../api/admin'
import type { AdminMe } from '../api/admin'

type Status = 'loading' | 'anon' | 'authed'

interface AdminSessionValue {
  status: Status
  user: AdminMe | null
}

const AdminSessionContext = createContext<AdminSessionValue>({ status: 'loading', user: null })

export function useAdminSession(): AdminSessionValue {
  return useContext(AdminSessionContext)
}

// Fetches /me once on mount. No auto-refresh/polling — a session that
// expires mid-use surfaces as a 401 on the next API call (handled by each
// page's own error state) rather than a background timer.
export function AdminSessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading')
  const [user, setUser] = useState<AdminMe | null>(null)

  useEffect(() => {
    let cancelled = false
    adminMe()
      .then((me) => {
        if (cancelled) return
        setUser(me)
        setStatus('authed')
      })
      .catch(() => {
        if (!cancelled) setStatus('anon')
      })
    return () => {
      cancelled = true
    }
  }, [])

  return <AdminSessionContext.Provider value={{ status, user }}>{children}</AdminSessionContext.Provider>
}

// Gate for every real admin page: sends to /login (an in-app screen, not
// straight to Google) when there's no session — so logging out or a session
// expiring lands on something explicable rather than an immediate,
// unexplained redirect to Google's account picker.
export function RequireAdminAuth({ children }: { children: ReactNode }) {
  const { status } = useAdminSession()
  const location = useLocation()

  if (status === 'loading') return null
  if (status === 'anon') {
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  return <>{children}</>
}

// The DocAI testing surface makes real outbound Salesforce calls, so it is
// restricted to full admins rather than the console's read-only viewer role.
export function RequireAdminRole({ children }: { children: ReactNode }) {
  const { status, user } = useAdminSession()
  if (status === 'loading') return null
  if (status !== 'authed' || user?.role !== 'admin') {
    return <Navigate to="/accounts" replace />
  }
  return <>{children}</>
}

// The inverse: once signed in, /login itself should not be reachable — bounce
// to /accounts instead of showing "Sign in" to someone already signed in.
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { status } = useAdminSession()
  if (status === 'loading') return null
  if (status === 'authed') return <Navigate to="/accounts" replace />
  return <>{children}</>
}
